import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { CHAT_TEXT } from '../../../constants/chat';
import { SkylarqAvatar } from './SkylarqAvatar';

interface EditableCriteriaSectionProps {
  criteria: string[];
  editingIndex: number | null;
  editingValue: string;
  hasEdited: boolean;
  isCreating: boolean;
  editInputRef: React.RefObject<HTMLTextAreaElement>;
  onEdit: (index: number) => void;
  onSave: () => void;
  onCancel: () => void;
  onRemove: (index: number) => void;
  onRun: () => void;
  onEditingValueChange: (value: string) => void;
}

/**
 * Editable criteria section shown when search returns no results
 * Allows users to modify criteria and retry the search
 */
export const EditableCriteriaSection: React.FC<EditableCriteriaSectionProps> = ({
  criteria,
  editingIndex,
  editingValue,
  hasEdited,
  isCreating,
  editInputRef,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  onRun,
  onEditingValueChange,
}) => {
  const navigate = useNavigate();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Skip if IME composition is in progress (for CJK input methods)
    if (e.nativeEvent.isComposing) {
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSave();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="flex gap-3 animate-fadeIn">
      <SkylarqAvatar />
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{CHAT_TEXT.BRAND_NAME_UPPER}</p>
        </div>

        {/* Header */}
        <p className="text-[14px] text-gray-700 mb-3">
          You can edit your search criteria below and try again:
        </p>

        {/* Editable criteria list */}
        <div className="space-y-2 mb-4">
          {criteria.map((criterion, index) => (
            <div
              key={index}
              role={editingIndex !== index ? 'button' : undefined}
              tabIndex={editingIndex !== index ? 0 : undefined}
              onClick={() => editingIndex !== index && onEdit(index)}
              onKeyDown={(e) => {
                if (editingIndex !== index && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onEdit(index);
                }
              }}
              className={`group flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100 ${
                editingIndex !== index
                  ? 'cursor-pointer hover:border-gray-200 hover:bg-gray-100/50 transition-all focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-50'
                  : ''
              }`}
            >
              {editingIndex === index ? (
                <div className="flex-1">
                  <textarea
                    ref={editInputRef}
                    value={editingValue}
                    onChange={(e) => onEditingValueChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={onSave}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-3 py-2 text-[13px] bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-gray-300 resize-none"
                    rows={3}
                  />
                </div>
              ) : (
                <>
                  <span className="text-[12px] text-gray-400 font-mono mt-0.5 flex-shrink-0">{index + 1}</span>
                  <p className="flex-1 text-[13px] text-gray-700">{criterion}</p>
                  <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 group-focus:opacity-100 group-focus-within:opacity-100 transition-opacity">
                    <Pencil className="w-3.5 h-3.5 text-gray-400" />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(index);
                      }}
                      className="p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                      aria-label="Remove criterion"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onRun}
            disabled={criteria.length === 0 || isCreating}
            loading={isCreating}
            size="sm"
            className="rounded-full"
          >
            {hasEdited ? 'Run edited search' : 'Retry search'}
          </Button>
          <Button
            onClick={() => navigate('/datasets')}
            disabled={isCreating}
            variant="secondary"
            size="sm"
            className="rounded-full"
          >
            Start new search
          </Button>
        </div>
      </div>
    </div>
  );
};
